import { useState } from 'react';
import { Bell, Bot, Command, ListTodo, Menu, Search, Wifi, WifiOff, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { ApiStatus } from '../AppLayout';
import { ActivityCenterDrawer, ActivityCenterTab } from '../activity/ActivityCenterDrawer';
import { useActivityCenter } from '../../features/ActivityCenter';
import { meshDevicePath } from '../../lib/meshcentral';
import { AiAssistantDrawer } from '../ai/AiAssistantDrawer';

interface TopbarProps {
  onOpenPalette: () => void;
  onOpenMenu: () => void;
  apiStatus: ApiStatus;
}

export function Topbar({ onOpenPalette, onOpenMenu, apiStatus }: TopbarProps) {
  const navigate = useNavigate();
  const { data, unreadCount } = useActivityCenter();
  const [centerOpen, setCenterOpen] = useState(false);
  const [centerTab, setCenterTab] = useState<ActivityCenterTab>('tasks');
  const [aiOpen, setAiOpen] = useState(false);
  const openCenter = (tab: ActivityCenterTab) => { setCenterTab(tab); setCenterOpen(true); };

  return (
    <><header className="sticky top-0 z-20 flex h-[72px] items-center gap-2 border-b border-border bg-white/90 px-4 backdrop-blur-xl sm:gap-3 sm:px-6">
      <button onClick={onOpenMenu} className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground lg:hidden" aria-label="Открыть меню">
        <Menu className="h-4 w-4" />
      </button>

      <button onClick={() => openCenter('tasks')} className="relative flex h-10 items-center gap-2 rounded-lg border border-border bg-card px-3 text-sm font-semibold text-foreground transition-colors hover:border-primary/40 hover:bg-accent" aria-label={`Центр задач. Активных: ${data?.activeCount || 0}`}>
        <ListTodo className="h-4 w-4 text-primary" /><span className="hidden lg:inline">Задачи</span>
        <span className="min-w-5 rounded-full bg-primary px-1.5 py-0.5 text-center text-[10px] font-bold text-primary-foreground">{data?.activeCount || 0}</span>
      </button>

      <button onClick={() => openCenter('notifications')} className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-border bg-card text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground" aria-label={`Уведомления. Новых: ${unreadCount}`}>
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && <span className="absolute -right-1.5 -top-1.5 min-w-5 rounded-full bg-destructive px-1 py-0.5 text-center text-[9px] font-bold leading-4 text-white">{unreadCount > 99 ? '99+' : unreadCount}</span>}
      </button>
      <button onClick={() => setAiOpen(true)} className="flex h-10 shrink-0 cursor-pointer items-center gap-2 rounded-lg border border-cyan-200 bg-cyan-50 px-3 text-sm font-semibold text-cyan-900 transition-colors hover:border-cyan-300 hover:bg-cyan-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-600" aria-label="Открыть ИИ-помощника">
        <Bot className="h-4 w-4 text-cyan-700" /><span className="hidden 2xl:inline">ИИ-помощник</span>
      </button>
      <span className="sr-only" aria-live="polite">Активных задач: {data?.activeCount || 0}. Новых уведомлений: {unreadCount}.</span>
      <div className="hidden xl:block">
        <p className="text-sm font-semibold text-foreground">Панель администратора TelemedConnect</p>
      </div>

      <button
        onClick={onOpenPalette}
        className="ml-auto flex min-w-0 flex-1 items-center gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground sm:max-w-md"
      >
        <Search className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-left">Найти раздел или команду...</span>
        <kbd className="hidden items-center gap-0.5 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[11px] sm:flex"><Command className="h-3 w-3" />K</kbd>
      </button>

      <div className={apiStatus === 'online' ? 'flex items-center gap-2 rounded-lg border border-success/20 bg-success/10 px-3 py-2 text-sm font-medium text-success' : apiStatus === 'offline' ? 'flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive' : 'flex items-center gap-2 rounded-lg border border-warning/20 bg-warning/10 px-3 py-2 text-sm font-medium text-warning'}>
        {apiStatus === 'online' ? <Wifi className="h-4 w-4" /> : apiStatus === 'offline' ? <WifiOff className="h-4 w-4" /> : <Loader2 className="h-4 w-4 animate-spin" />}
        <span className="hidden md:inline">{apiStatus === 'online' ? 'API подключён' : apiStatus === 'offline' ? 'API недоступен' : 'Проверка API'}</span>
      </div>
    </header>
    <ActivityCenterDrawer
      open={centerOpen}
      initialTab={centerTab}
      onClose={() => setCenterOpen(false)}
      onOpenDevice={(nodeId) => { setCenterOpen(false); navigate(meshDevicePath(nodeId)); }}
    />
    <AiAssistantDrawer open={aiOpen} onClose={() => setAiOpen(false)} />
    </>
  );
}
